/**
 * uploadWorkspaceBlob — sl-ngy4.3.4.
 *
 * TUS direct upload wrapped as a promise returning a complete `BlobReference`
 * (kind/blobId/filename/mimeType/size). This is the frontend source of truth
 * for file → BlobReference conversion outside editor DI.
 */

import type { WorkspaceBlobContext } from '@affine/core/modules/workspace/utils/blob-cache';
import { uploadWorkspaceFileViaTus } from '@affine/core/modules/workspace/utils/tus-direct-upload';
import { fileWithResolvedMimeType } from '@affine/core/utils/blob-file-type';
import type { BlobReference } from '@streamlain/field-types';

export async function uploadWorkspaceBlob(
  workspaceId: string,
  file: File,
  options: { docId?: string | null; context: WorkspaceBlobContext | undefined }
): Promise<BlobReference> {
  if (!options.context)
    throw new Error('Initialized workspace upload scope required');
  options.context.assertActive();
  const uploadFile = await fileWithResolvedMimeType(file);
  const blobId = await uploadWorkspaceFileViaTus({
    workspaceId,
    context: options.context,
    file: uploadFile,
    ...(options.docId ? { docId: options.docId } : {}),
  });
  if (!blobId) {
    throw new Error('Streamable blob upload requires tus direct upload');
  }

  return {
    kind: 'blob',
    blobId,
    filename: uploadFile.name,
    mimeType: uploadFile.type || 'application/octet-stream',
    size: uploadFile.size,
  };
}
